<template>
  <div class="invoice-add-container">
    <b-row>
      <b-col :lg="12">
        <div class="drip-form-container">
          <form @submit.prevent="submitInvoiceInfo" novalidate>
            <div class="drip-form">
              <b-row>
                <b-col>
                  <h3 class="title-slash">
                    {{ $t('invoices.add.titles.invoiceInfo') }}
                  </h3>
                </b-col>
              </b-row>
              <b-row>
                <b-col :sm="10">
                  <b-form-group
                    class="dc-group"
                    :class="{
                      'is-invalid': errors.has('buyer'),
                      'is-empty': importer === '' || importer === null
                    }"
                  >
                    <!-- Pass an 'id' with multiselect to make the focus method work -->
                    <label>
                      <span>{{ $t('invoices.add.helpers.buyer.label') }}</span>
                    </label>
                    <b-input-group class="dc-input-group">
                      <multiselect
                        v-tabindex
                        :disabled="this.id !== '' || loading"
                        v-validate="'required'"
                        data-vv-as="Buyer"
                        v-model="importer"
                        :options="exporterData.clients_locked"
                        track-by="importer"
                        name="buyer"
                        @input="buyerChange"
                        label="importer"
                        :placeholder="
                          $t('invoices.add.helpers.buyer.placeHolder')
                        "
                        exact
                      >
                        <template slot="option" slot-scope="props">
                          {{ props.option.importer }}
                          <small class="text-uppercase"
                            >({{
                              $t(
                                `buyers.index.status['${props.option.new_portal_status}']`
                              )
                            }})</small
                          >
                        </template>
                        <template slot="noResult">
                          <div>
                            <span>{{
                              $t('invoices.add.helpers.buyer.notFound')
                            }}</span>
                          </div>
                        </template>
                        <span class="action-input" slot="caret"
                          ><b-icon-chevron-down font-scale="0.9"
                        /></span>
                      </multiselect>
                    </b-input-group>
                    <span class="error">{{ errors.first('buyer') }}</span>
                  </b-form-group>
                </b-col>
                <b-col :sm="10">
                  <b-form-group
                    class="dc-group"
                    :class="{
                      'is-invalid': errors.has('reference'),
                      'is-empty': form.reference === ''
                    }"
                  >
                    <label>
                      <span>{{
                        $t('invoices.add.helpers.invoiceNo.label')
                      }}</span>
                    </label>
                    <b-form-input
                      v-validate="'required'"
                      name="reference"
                      data-vv-as="Invoice number"
                      type="text"
                      :placeholder="
                        $t('invoices.add.helpers.invoiceNo.placeHolder')
                      "
                      v-model="form.reference"
                    ></b-form-input>
                    <span class="error">
                      {{ errors.first('reference') }}
                    </span>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col :sm="5">
                  <b-form-group
                    class="dc-group"
                    :class="{
                      'is-invalid': errors.has('invoice_date'),
                      'is-empty': dates.invoice_date === ''
                    }"
                  >
                    <label>
                      <span>{{
                        $t('invoices.add.helpers.invoiceDate.label')
                      }}</span>
                    </label>
                    <div class="date-picker position-relative">
                      <date-picker
                        autocomplete="off"
                        name="invoice_date"
                        data-vv-as="Invoice date"
                        v-model="dates.invoice_date"
                        :placeholder="
                          $t('invoices.add.helpers.invoiceDate.placeHolder')
                        "
                        ref="invoiceDate"
                        :config="options"
                        v-validate="'required'"
                      >
                      </date-picker>
                      <span
                        class="action-input"
                        slot="caret"
                        @click="$refs.invoiceDate.$el.focus()"
                        ><b-icon-calendar-2-event-fill font-scale="0.9"
                      /></span>
                    </div>
                    <span class="error">
                      {{ errors.first('invoice_date') }}
                    </span>
                  </b-form-group>
                </b-col>
                <b-col :sm="5">
                  <b-form-group
                    class="form-buyer dc-group"
                    :class="{
                      'is-invalid': errors.has('currency'),
                      'is-empty': currency === '' || currency === null
                    }"
                  >
                    <!-- Pass an 'id' with multiselect to make the focus method work -->
                    <label>
                      <span>{{
                        $t('invoices.add.helpers.currency.label')
                      }}</span>
                    </label>
                    <b-input-group class="dc-input-group">
                      <multiselect
                        v-tabindex
                        v-validate="'required'"
                        data-vv-as="Currency"
                        v-model="currency"
                        :options="currencies"
                        name="currency"
                        label="value"
                        track-by="key"
                        :selectLabel="$t('multiselect.selectLabel')"
                        :deselectLabel="$t('multiselect.deSelectLabel')"
                        :selectedLabel="$t('multiselect.selectedLabel')"
                        :placeholder="
                          $t('invoices.add.helpers.currency.placeHolder')
                        "
                      >
                        <span class="action-input" slot="caret"
                          ><b-icon-chevron-down font-scale="0.9"
                        /></span>
                      </multiselect>
                    </b-input-group>
                    <span class="error">{{ errors.first('currency') }}</span>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col :sm="5">
                  <b-form-group
                    class="dc-group"
                    :class="{
                      'is-invalid': errors.has('gross_value'),
                      'is-empty':
                        form.gross_value === '' || form.gross_value === 0
                    }"
                  >
                    <label>
                      <span>{{
                        $t('invoices.add.helpers.grossValue.label')
                      }}</span>
                    </label>
                    <b-input-group :prepend="curSym">
                      <b-form-input
                        name="gross_value"
                        type="text"
                        v-validate="'required_gross'"
                        data-vv-as="Gross value"
                        v-currency-mask
                        :placeholder="
                          $t('invoices.add.helpers.grossValue.placeHolder')
                        "
                        v-model="form.gross_value"
                      >
                        <span class="action-input" slot="caret"
                          ><b-icon-calendar-2-event-fill font-scale="0.9"
                        /></span>
                      </b-form-input>
                    </b-input-group>
                    <span v-if="errors.has('gross_value')" class="error">
                      {{ errors.first('gross_value') }}
                    </span>
                  </b-form-group>
                </b-col>
                <b-col :sm="5">
                  <b-form-group
                    class="dc-group"
                    :class="{
                      'is-invalid': errors.has('advance_received'),
                      'is-empty':
                        form.advance_received === '' ||
                        form.advance_received === 0
                    }"
                  >
                    <label>
                      <span>{{
                        $t('invoices.add.helpers.advanceReceived.label')
                      }}</span>
                    </label>
                    <b-input-group :prepend="curSym">
                      <b-form-input
                        name="advance_received"
                        type="text"
                        v-currency-mask
                        v-validate="'advance_received_rule'"
                        data-vv-as="Advance received"
                        :placeholder="
                          $t('invoices.add.helpers.advanceReceived.placeHolder')
                        "
                        v-model="form.advance_received"
                      ></b-form-input>
                    </b-input-group>
                    <span class="error">
                      {{ errors.first('advance_received') }}
                    </span>
                  </b-form-group>
                </b-col>
              </b-row>
              <b-row>
                <b-col :sm="5">
                  <b-form-group class="dc-group">
                    <label>
                      <span class="label-disabled">{{
                        $t('invoices.add.helpers.netValue.label')
                      }}</span>
                    </label>
                    <b-input-group :prepend="curSym">
                      <b-form-input
                        type="text"
                        disabled
                        v-model="calculatedValue"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>
                <b-col :sm="5">
                  <b-form-group class="dc-group">
                    <label>
                      <span class="label-disabled">{{
                        $t('invoices.add.helpers.maxValue.label')
                      }}</span>
                    </label>
                    <b-input-group :prepend="curSym">
                      <b-form-input
                        type="text"
                        disabled
                        v-model="maxValue"
                      ></b-form-input>
                    </b-input-group>
                    <span class="note">
                      {{ $t('invoices.add.helpers.maxValue.note') }}
                    </span>
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
            <div class="drip-form-actions">
              <br />
              <b-row>
                <b-col>
                  <b-link class="mr-3" @click="saveDraft()">
                    {{ $t('invoices.add.actions.save') }}
                  </b-link>
                  <form-button
                    :loading="infoSaving"
                    type="submit"
                    variant="secondary"
                  >
                    <span class="d-md-inline-block d-none">{{
                      $t('invoices.add.actions.next')
                    }}</span>
                    <b-icon-chevron-right class="d-md-none" />
                  </form-button>
                </b-col>
              </b-row>
            </div>
          </form>
        </div>
      </b-col>
    </b-row>
    <on-hold-modal
      :on-hold-modal="modal"
      :add-buyer-id="parseInt(buyerId)"
      @modalClose="cancelAddInvoice"
      @modalOk="setForm(importer)"
    />

    <dummy-invoice
      :tour-status="tourStatus"
      :payment-term="paymentTerm"
      :assigned="assigned"
      :bank-details="bankDetails"
      @tourStatusChanged="
        (e) => {
          tourStatus = e
        }
      "
    />

    <b-modal
      v-if="hasPermissionToAddBank"
      ref="addBankModal"
      hide-header
      centered
      hide-footer
    >
      <h4>{{ $t('addBank.notAdded') }}</h4>
      <p>{{ $t('addBank.notSupported') }}</p>
      <b-button
        variant="primary mr-2"
        @click="addBank()"
        v-track-click
        id="invoices-upload-later"
      >
        {{ $t('addBank.title') }}
      </b-button>
      <b-button variant="secondary mr-2" @click="$refs.addBankModal.hide()">
        {{ $t('addBank.cancelButton') }}
      </b-button>
    </b-modal>
  </div>
</template>
<script>
import DummyInvoice from '@/components/DummyInvoice'
import FormButton from '@/components/common/FormButton'
import Multiselect from 'vue-multiselect'
import { mapActions } from 'vuex'
import DatePicker from 'vue-bootstrap-datetimepicker'
import { addInvoiceMixin } from '@/mixins/Invoice'
import OnHoldModal from '@/components/buyers/OnHoldModal/'
import invoiceService from '@/services/invoice'
import InputMasking from '@/helpers/input-masking'
import userService from '@/services/user'
import exportersService from '@/services/exporters'
import { ACTIONS, ENTITIES } from '@/utils/constants'

export default {
  props: ['saving', 'exporterData', 'form'],
  data() {
    return {
      meta: null,
      method: 'POST',
      id: '',
      tourStatus: false,
      paymentTerm: null,
      assigned: null,
      bankDetails: [],
      infoSaving: this.saving,
      currentStep: 'basic-info',
      nextStep: 'shipping-details',
      dates: {
        invoice_date: ''
      },
      importer: '',
      currency: null,
      loading: true,
      modal: false,
      banksList: {}
    }
  },
  mounted() {
    this.$mpTrack(`Add Invoice - Information Step Loaded`)
    this.getInvoiceMeta()
    this.getBankAccounts()
  },
  created() {
    this.$validator.extend('required_gross', {
      getMessage: (field) => this.$i18n.t('custom.gross_value.min_value'),
      validate: (value) => value !== '' && InputMasking.stripNaN(value) > 0
    })
    this.$validator.extend('advance_received_rule', {
      getMessage: (field) => this.$i18n.t('custom.advance_received.max_value'),
      validate: (value) =>
        InputMasking.stripNaN(this.form && this.form.gross_value) - 0.01 >
        InputMasking.stripNaN(value)
    })
  },
  computed: {
    // clientsLockedWithLimit () {
    //   return this.exporterData.clients_locked.filter(c => c.product_based_limit && c.product_based_limit > 0)
    // },
    calculatedValue() {
      const grossValue = InputMasking.stripNaN(
        (this.form && this.form.gross_value) || 0
      )
      const advanceReceived = InputMasking.stripNaN(
        (this.form && this.form.advance_received) || 0
      )
      if (advanceReceived >= 0 && grossValue >= 0) {
        return InputMasking.convertToCurrency(grossValue - advanceReceived)
      }
      return ''
    },
    maxValue() {
      if (this.exporterData && this.exporterData.clients_locked.length) {
        const calculatedVal = InputMasking.stripNaN(this.calculatedValue)
        const advanceRate = (this.importer && this.importer.advance_rate) || 0
        const advanceRatePercentile = parseFloat(advanceRate) / 100
        return this.importer
          ? InputMasking.convertToCurrency(
              parseFloat(calculatedVal * advanceRatePercentile)
                .toFixed(2)
                .replace(/[.,]00$/, '')
            )
          : 0
      }
      return 0
    },
    currencies() {
      if (!this.meta) {
        return []
      }
      return Object.keys(this.meta.CURRENCY_VALUES).map((key) => {
        return {
          key: key,
          value: this.meta.CURRENCY_VALUES[key]
        }
      })
    },
    curSym() {
      if (this.meta && this.currency) {
        this.getCurrency(this.currency.key)
        return this.meta.CURRENCY_SYMBOLS[this.currency.key]
      } else {
        return '$'
      }
    },
    buyerId() {
      return parseInt(this.$route.query.buyerId)
    },
    options() {
      return {
        format: 'DD/MM/YYYY',
        useCurrent: false,
        locale: this.$i18n.locale === 'es-MX' ? 'es' : 'en'
      }
    },
    isMx() {
      return this.$i18n.locale === 'es-MX' || this.$i18n.locale === 'en-MX'
    },
    hasPermissionToAddBank() {
      return (
        this.$store.getters.hasActionPermission(
          ENTITIES.BANK,
          ACTIONS.CREATE
        ) && !this.isMx
      )
      /**
       * TODO: this.$store.getters.hasActionPermission('bank', 'create')
       * ? below is the old code, the return statement just had !this.isMx
       * !this.isMx
       */
    }
  },
  methods: {
    buyerChange(importer) {
      if (
        importer &&
        importer.new_portal_status !== 'Funding Available' &&
        importer.invoices_total
      ) {
        this.modal = true
        return
      }
      this.setForm(importer)
    },
    setForm(importer) {
      this.modal = false
      this.currency = importer && this.getCurrency(importer.currency)
      if (importer && importer.invoices_total === 0) {
        // disabling add invoice coachmarks for mexico
        if (!this.$store.state.account.country === 'Mexico') {
          this.tourStatus = true
        }
        this.paymentTerm = importer.invoice_guide.payment_term
        this.assigned = importer.invoice_guide.assigned
        this.bankDetails = importer.invoice_guide.bank_details
      } else {
        this.tourStatus = false
        this.paymentTerm = null
        this.assigned = null
        this.bankDetails = null
      }
    },
    cancelAddInvoice() {
      if (this.$route.query.buyerId) {
        this.$router.push({ name: 'buyers' })
        return
      }
      this.modal = false
      this.importer = ''
      this.currency = null
    },
    getCurrency(currency) {
      return this.currencies.find((ele) => {
        return currency === ele.key
      })
    },
    getInvoiceMeta() {
      return invoiceService.invoiceMeta().then((res) => {
        this.meta = res.data
      })
    },
    addBank() {
      this.$router.push({ name: 'addBankAccount' })
    },
    submitInvoiceInfo() {
      this.$mpTrack('Add Invoice - Information Step Next Clicked')
      this.form.gross_value = InputMasking.stripNaN(
        this.form && this.form.gross_value
      ).toString()
      this.form.advance_received = InputMasking.stripNaN(
        this.form && this.form.advance_received
      ).toString()
      this.$validator
        .validate()
        .then((result) => {
          if (result) {
            this.addInvoiceInfo()
          } else {
            this.$mpTrack(
              'Add Invoice - Information validation error - mandatory fields not filled'
            )
          }
        })
        .finally(() => {
          this.form.gross_value = InputMasking.convertToCurrency(
            this.form && this.form.gross_value
          )
          this.form.advance_received = InputMasking.convertToCurrency(
            this.form && this.form.advance_received
          )
        })
    },
    addInvoiceInfo() {
      this.$mpTrack('Add Invoice - Information Step Validated')
      this.infoSaving = true
      this.setDates()
      // workaround for multiselect object. couldn't figure out any other possible way
      this.form.importer_user_id = this.importer.importer_user_id
      this.form.currency = this.currency.key
      this.form.value = InputMasking.stripNaN(this.calculatedValue)
      this.form.stage = 'draft'
      return invoiceService
        .modifyInvoices(this.method, this.id, {
          invoice: this.form
        })
        .then((res) => {
          this.infoSaving = false
          this.$mpTrack('Add Invoice - Invoice Information Added')
          this.setFormData(this.form)
          this.setStep(this.nextStep)
          this.navigate(
            'invoicesAdd',
            res.data.id || this.$route.params.id,
            this.currentStep
          )
          this.navigate(
            'invoicesAdd',
            res.data.id || this.$route.params.id,
            this.nextStep
          )
        })
        .catch((res) => {
          this.$mpTrack('Add Invoice - Invoice Information Error', {
            error: res
          })
          if (
            res &&
            res.response &&
            res.response.status &&
            res.response.status === 422 &&
            res.response.data.message.includes('Reference already added.')
          ) {
            this.$validator.errors.add({
              field: 'reference',
              msg: this.$t('invoices.add.form.response.invoiceExists')
            })
          }
          this.infoSaving = false
        })
    },
    saveDraft() {
      if (this.importer) {
        this.infoSaving = true
        this.setDates()
        // workaround for multiselect object. couldn't figure out any other possible way
        this.form.importer_user_id = this.importer.importer_user_id
        this.form.currency = this.currency ? this.currency.key : null
        this.form.value = InputMasking.stripNaN(this.calculatedValue) || null
        this.form.gross_value = InputMasking.stripNaN(
          this.form && this.form.gross_value
        )
        this.form.advance_received = InputMasking.stripNaN(
          this.form.advance_received
        )
        this.form.stage = 'draft'
        return invoiceService
          .modifyInvoices(this.method, this.id, {
            invoice: this.form
          })
          .then((res) => {
            this.infoSaving = false
            this.$mpTrack('Add Invoice - Saved as Draft')
            this.$router.push({
              name: 'invoices'
            })
            this.navigate('invoices')
          })
          .catch((err) => {
            if (
              err &&
              err.response &&
              err.response.status &&
              err.response.status === 422 &&
              err.response.data.message.reference
            ) {
              this.$validator.errors.add({
                field: 'reference',
                msg: this.$t('invoices.add.form.response.invoiceExists')
              })
            }
            this.infoSaving = false
          })
      } else {
        this.$validator.errors.add({
          field: 'buyer',
          msg: 'Please select a buyer to save as draft'
        })
      }
    },
    checkInvoiceStatus() {
      if (this.$route.params.id) {
        this.id = this.$route.params.id
        this.method = 'PUT'
        this.loadInvoice()
      }
    },
    loadInvoice() {
      this.infoSaving = true
      return invoiceService
        .fetchInvoicesById(this.$route.params.id, {
          include: ['form_data']
        })
        .then((res) => {
          this.infoSaving = false
          this.importer = this.getBuyerFromId(
            res.data.form_data.importer_user_id
          )
          this.currency = this.getCurrency(res.data.form_data.currency)
          res.data.form_data.gross_value = InputMasking.convertToCurrency(
            res.data.form_data.gross_value
          )
          res.data.form_data.advance_received = InputMasking.convertToCurrency(
            res.data.form_data.advance_received
          )
          this.setFormData(res.data.form_data)
          this.getDates(res.data.form_data)
        })
    },
    preselectBuyer() {
      const importers = this.exporterData.clients_locked.filter((buyer) => {
        return parseInt(buyer.id) === parseInt(this.$route.query.buyerId)
      })
      this.importer = importers[0]
      this.buyerChange(importers[0])
    },
    ...mapActions(['setStep', 'setFormData']),

    checkVerifiedBank(value) {
      const dataDependency =
        this.banksList &&
        this.exporterData &&
        this.banksList.Verified &&
        this.exporterData.bank_details
      const mxCurrency = {
        mxn: 'pesos'
      }
      const bank =
        dataDependency &&
        [
          ...this.banksList.Verified,
          ...this.banksList['Pending Verification']
        ].find(({ id: verifiedBankID }) =>
          this.exporterData.bank_details.find((exporterBank) => {
            const exporterBankCurrency = exporterBank.currency.toLowerCase()
            const selectedCurrency = value.toLowerCase()

            return (
              verifiedBankID === exporterBank.id &&
              (exporterBankCurrency === selectedCurrency ||
                selectedCurrency === mxCurrency[exporterBankCurrency])
            )
          })
        )

      if (!bank) {
        this.openAddBankModal()
      }
    },

    openAddBankModal() {
      this.$refs.addBankModal && this.$refs.addBankModal.show()
    },

    getBankAccounts() {
      this.loading = true
      userService.fetchAccessToken().then((res) => {
        exportersService
          .fetchBankAccounts(
            res.data.token,
            this.$store.state.account.accountId
          )
          .then((r) => {
            this.banksList = (r && r.data) || {}
          })
          .catch(console.error)
      })
    }
  },
  watch: {
    currency: {
      handler(currency) {
        const currencyValue = (currency && currency.value) || ''
        this.checkVerifiedBank(currencyValue)
      }
    },
    exporterData() {
      this.loading = false
      this.$mpTrack('Add Invoice - Select Buyer loaded')
      if (this.buyerId) {
        this.preselectBuyer()
      }
    }
  },
  components: {
    FormButton,
    Multiselect,
    DatePicker,
    DummyInvoice,
    OnHoldModal
  },
  mixins: [addInvoiceMixin]
}
</script>
